import {
  Button,
  ButtonWidth,
  DateFormat,
  formatDate,
  formatName,
  formatTimeFromMinutes,
  IconRefresh,
} from '@leland-dev/leland-ui-library';
import Image from 'next/image';
import Link from 'next/link';
import { type FC } from 'react';

import {
  SkuFamily,
  SkuPhaseType,
  type SkuTier,
} from '../../__generated-gql-types__/globalTypes';
import { getCoachUrl } from '../../utils/coach';
import { SUBSCRIBER_DUMMY_COACH_URN } from '../../utils/constants';
import { getPlanCustomerPageUrl } from '../../utils/package';
import { TierBadge } from '../TierBadge';

import { type AssignCoachingCardOrderFragment } from './__generated-gql-types__/AssignCoachingCard.generated';

interface AssignCoachingCardProps {
  order: AssignCoachingCardOrderFragment;
  onAssignToACoach?: (order: AssignCoachingCardOrderFragment) => void;
  hasValidCoach?: boolean;
  handleGetMatched?: () => void;
  compact?: boolean;
}

const OrderTier: FC<{ tier: SkuTier }> = ({ tier }) => {
  return <TierBadge hideTooltip tier={tier} goal={null} />;
};

export const AssignCoachingCard: FC<AssignCoachingCardProps> = ({
  order,
  onAssignToACoach,
  hasValidCoach,
  handleGetMatched,
  compact,
}) => {
  const { hourlyOrder, subscriptionOrder, packageOrder, coach, createdAt } =
    order;

  const isLelandPackage = coach.id === SUBSCRIBER_DUMMY_COACH_URN;
  const orderTierBadge = (
    <OrderTier
      tier={
        packageOrder?.prices?.[0]?.tier ??
        subscriptionOrder?.customerSubscription.sku.tier ??
        order.hourlyOrder?.sku?.tier ??
        coach.tier
      }
    />
  );
  const isOrderTrialPhase =
    subscriptionOrder?.phaseType === SkuPhaseType.FREE_TRIAL;
  return (
    <div className={`flex gap-2 ${compact ? 'p-4' : 'p-6'} justify-between`}>
      <div className="flex w-full flex-col">
        <div className="flex flex-col text-leland-gray-light">
          <div className="flex justify-between">
            {packageOrder?.imageLink && !compact ? (
              <Image
                className="mb-2 aspect-entity-image w-20 shrink-0 grow-0 rounded-md object-cover"
                src={packageOrder.imageLink}
                alt={packageOrder.name || 'Package Image'}
                width={191}
                height={100}
              />
            ) : null}
            <p className="text-lg font-medium text-leland-gray-dark">
              {hourlyOrder
                ? formatTimeFromMinutes(hourlyOrder.timeInMinute)
                : subscriptionOrder
                  ? isOrderTrialPhase
                    ? 'Trial Coaching Membership'
                    : formatTimeFromMinutes(subscriptionOrder.timeInMinute)
                  : null}
            </p>
            {compact || subscriptionOrder?.sku?.family === SkuFamily.LINKEDIN
              ? null
              : orderTierBadge}
          </div>
          {packageOrder?.prices?.[0]?.packageCoachingTime ? (
            <p className="text-lg font-medium text-leland-gray-dark">
              {packageOrder.prices[0].packageCoachingTime.estimate
                ? 'Comprehensive Package'
                : packageOrder.prices[0].packageCoachingTime.exactTimeInMinute
                  ? formatTimeFromMinutes(
                      packageOrder.prices[0].packageCoachingTime
                        .exactTimeInMinute,
                    )
                  : null}
            </p>
          ) : null}
          <div className={`${hourlyOrder || compact ? '' : 'my-3'}`}>
            {subscriptionOrder?.customerSubscription.nextCreditGrantAt ? (
              <p className="flex items-center gap-2">
                <IconRefresh className="size-3" />
                <span>
                  Subscription, renews{' '}
                  {formatDate(
                    subscriptionOrder.customerSubscription.nextCreditGrantAt,
                  )}
                </span>
              </p>
            ) : packageOrder ? (
              <Link
                className="underline"
                target="_blank"
                href={getPlanCustomerPageUrl({
                  categorySlug: packageOrder.category?.slug,
                  packageSlug: packageOrder.slug,
                  coachTier: packageOrder.prices?.[0]?.tier,
                  coachId: isLelandPackage ? undefined : coach.id,
                })}
                id="assign_coaching_card--package_page"
              >
                {packageOrder.name}
              </Link>
            ) : null}
            <p className="flex items-center gap-1 text-leland-gray-extra-light">
              {packageOrder?.prices?.[0].packageCoachingTime?.estimate &&
              packageOrder?.prices?.[0]?.sku ? (
                <>
                  {packageOrder?.prices[0].sku} &middot; Purchased{' '}
                  {formatDate(createdAt, DateFormat.NUMBER_STANDARD)}
                </>
              ) : null}
            </p>
            {onAssignToACoach ? (
              isOrderTrialPhase ? (
                <div>
                  {order.submittedAt ? (
                    <p className="text-leland-gray-extra-light">
                      Purchased{' '}
                      {formatDate(order.submittedAt, DateFormat.STANDARD)}
                    </p>
                  ) : null}
                  <p>Pending coach match</p>
                </div>
              ) : coach.id === SUBSCRIBER_DUMMY_COACH_URN ? (
                hasValidCoach ? (
                  <div className="mt-3 w-2/3">
                    <Button
                      label="Assign to a coach"
                      onClick={() => onAssignToACoach(order)}
                      id="assign_coaching_card--assign_to_a_coach"
                    />
                  </div>
                ) : handleGetMatched ? (
                  <div className="mt-3 w-2/3">
                    <Button
                      label="Get matched with a coach"
                      width={ButtonWidth.AUTO}
                      onClick={() => handleGetMatched()}
                      id="assign_coaching_card--get_matched_with_a_coach"
                    />
                  </div>
                ) : null
              ) : (
                <p className="text-leland-gray-light">
                  Pending request sent to{' '}
                  <Link
                    href={getCoachUrl({
                      coachSlug: coach.slug,
                      categorySlug: null,
                    })}
                    target="_blank"
                    className="underline"
                    id="assign_coaching_card--pending_coach_profile"
                  >
                    {formatName({
                      firstName: coach.user.firstName,
                      lastName: coach.user.lastName,
                    })}{' '}
                  </Link>
                </p>
              )
            ) : null}
          </div>
        </div>
      </div>
      {compact ? orderTierBadge : null}
    </div>
  );
};
